import React, { useMemo, useState } from "react";
import {
  Card,
  Employee,
  Shop,
  TagsDocument,
  FEATURE_FLAGS,
  HexColor,
} from "@/types/v2";
import { ColumnConfig } from "./types";
import { TableRow, TableCell } from "@/components/Base/Table";
import { getEmployeeName } from "@/lib/employees";
import { useStore } from "@/stores/useStore";
import { firestoreToDateTime } from "@/lib/utils";
import { format } from "date-fns";
import { useRoPanelParams } from "@/hooks/useRoPanelParams";
import { CardTimer } from "@/components/Dashboard/components/Card/Card.Timer";
import { CardStar } from "@/components/Dashboard/components/Card/Card.Star";
import { CardMenu } from "@/components/Dashboard/components/Card/Card.Menu";
import { NotesPopover } from "@/components/Dashboard/components/Card/NotesPopover";
import { AttachmentsPopover } from "@/components/Dashboard/components/Card/AttachmentsPopover";
import { ColorPicker } from "@/components/Dashboard/components/ColorPicker/ColorPicker";
import { useCardActions } from "@/hooks/useCardActions";
import { useRoles } from "@/hooks/useRoles";
import { useFeature } from "@/hooks/useFeatures";
import { cn } from "@/lib/utils";
import { DEFAULT_CARD_COLOR } from "@/lib/const";
import { getCardSection } from "@/lib/cards";
import { RenderTag } from "@/components/Dashboard/components/Card/Card.Tags";
import { sortTagsByShopOrder } from "@/lib/tags";
import { isEventFromPortal } from "@/lib/domEvents";

interface ListViewCardProps {
  card: Card;
  columns: ColumnConfig[];
  employees?: Employee[];
  shop?: Shop;
  tags?: TagsDocument;
  disabled?: boolean;
  onMoveToClick?: (card: Card) => void;
}

export const ListViewCard: React.FC<ListViewCardProps> = ({
  card,
  columns,
  employees: propEmployees,
  shop: propShop,
  tags: propTags,
  disabled,
  onMoveToClick,
}) => {
  const {
    employees: storeEmployees,
    shop: storeShop,
    tags: storeTags,
  } = useStore();
  const { selectCard } = useRoPanelParams();
  const [actionCellHovered, setActionCellHovered] = useState(false);

  // Use props first, fallback to store
  const employees = propEmployees || storeEmployees;
  const shop = propShop || storeShop;
  const tags = propTags || storeTags;
  // Note: tags are used for display in the menu components

  const { isViewOnly } = useRoles();
  const isCardColorEnabled = useFeature(FEATURE_FLAGS.isCardColorEnabled);
  const isCardNotesEnabled = useFeature(FEATURE_FLAGS.isCardNotesEnabled);
  const isAttachmentsEnabled = useFeature(FEATURE_FLAGS.isAttachmentsEnabled);

  const {
    handleTimerStart,
    handleTimerPause,
    handleTimerReset,
    handleTimerStop,
    handleToggleStarred,
    handleColorChange,
  } = useCardActions(card.id);

  const formatCellValue = (
    column: ColumnConfig,
    card: Card
  ): React.ReactNode => {
    const { field, formatter } = column;

    if (formatter) {
      return formatter(card[field as keyof Card], card);
    }

    switch (field) {
      case "primaryField":
        return shop ? getCardSection(card, shop, "primaryField") : "";

      case "secondaryField":
        return shop ? getCardSection(card, shop, "secondaryField") : "";

      case "tertiaryField":
        return shop ? getCardSection(card, shop, "tertiaryField") : "";

      case "technicianId":
        return getEmployeeName(card.technicianId, employees ?? []);

      case "serviceWriterId":
        return getEmployeeName(card.serviceWriterId, employees ?? []);

      case "currentContainer":
        const container = shop?.containers?.find(
          (c) => c.id === card.currentContainer
        );
        return container?.displayName || card.currentContainer;

      case "timer":
        return (
          <div className="flex items-center justify-center">
            <CardTimer
              onStart={handleTimerStart}
              onStop={handleTimerStop}
              onReset={handleTimerReset}
              onPause={handleTimerPause}
              startTime={firestoreToDateTime(card.timer?.timeStarted)}
              accumulatedTime={card.timer?.accumulatedTime}
              limit={
                card.timer?.timeLimitInMinutes
                  ? parseFloat(card.timer.timeLimitInMinutes) * 60 * 1000
                  : undefined
              }
              state={card.timer?.state}
              disabled={disabled || isViewOnly}
              className="m-0 h-auto w-auto p-1 text-base"
              timerClassName="text-sm text-white"
              timerSecondsClassName="text-sm"
              iconSize={20}
              triggerSize="default"
            />
          </div>
        );

      case "tags": {
        // Shop tag order is the shared display order, so apply it before the
        // slice — otherwise which two chips survive truncation depends on the
        // order the user happened to pick them in.
        const orderedTags = sortTagsByShopOrder(card.tags ?? [], tags?.tags);
        return orderedTags.length ? (
          <div className="flex flex-wrap gap-1">
            {orderedTags.slice(0, 2).map((tag, index) => {
              const shopTag = tags?.tags?.find((t) => t.label === tag);

              if (shopTag) {
                return <RenderTag key={index} tag={shopTag} label={tag} />;
              }
              return (
                <span
                  key={index}
                  className="inline-flex items-center rounded-full bg-blue-100 px-2 py-1 text-xs text-blue-800"
                >
                  {tag}
                </span>
              );
            })}
            {orderedTags.length > 2 && (
              <span className="text-xs text-gray-500">
                +{orderedTags.length - 2} more
              </span>
            )}
          </div>
        ) : null;
      }

      case "promisedTime":
        if (card.promisedTime) {
          try {
            const date = firestoreToDateTime(card.promisedTime);
            return date ? format(date, "Pp") : "";
          } catch {
            return "";
          }
        }
        return null;

      case "totalLaborHours":
        if (card.totalLaborHours) {
          return `${card.totalLaborHours.estimatedLabor}h / ${card.totalLaborHours.loggedLabor}h`;
        }
        return null;

      default:
        const value = card[field as keyof Card];
        return value ? String(value) : "";
    }
  };

  const visibleColumns = columns.filter((col) => col.visible);

  const handleRowClick = (e: React.MouseEvent<HTMLElement>) => {
    // This row mounts CardMenu, whose dialogs portal to document.body while
    // staying React children of this handler. The allowlist below catches their
    // buttons, but a click on the dialog overlay or its body text has no button
    // ancestor and would otherwise select the row. (WEBV3-161)
    if (isEventFromPortal(e)) {
      return;
    }

    // Don't navigate if clicking on interactive elements
    const target = e.target as HTMLElement;
    if (
      target.closest("button") ||
      target.closest('[role="button"]') ||
      target.closest("input") ||
      target.closest("[data-action-cell]")
    ) {
      return;
    }

    if (!shop) return;

    // Ctrl/Cmd+click still opens the full card page in a new tab; a plain click
    // opens the RO details side panel (matches board card-click behavior).
    if (e.ctrlKey || e.metaKey) {
      window.open(`/${shop.id}/cards/${card.id}`, "_blank");
    } else {
      selectCard(card.id);
    }
  };

  const handleStarClick = () => {
    if (!disabled && !isViewOnly) {
      handleToggleStarred(!card.isStarred);
    }
  };

  // Add actions column to the visible columns
  const columnsWithActions = useMemo(() => {
    const hasActionsColumn = visibleColumns.some((col) => col.id === "actions");
    if (hasActionsColumn) {
      return visibleColumns;
    }

    // Add actions column at the end
    return [
      ...visibleColumns,
      {
        id: "actions",
        field: "actions",
        label: "",
        width: 120,
        minWidth: 120,
        sortable: false,
        filterable: false,
        visible: true,
        fixed: true,
        align: "center" as const,
      } as ColumnConfig,
    ];
  }, [visibleColumns]);

  return (
    <TableRow
      className="cursor-pointer border-b transition-colors hover:bg-muted/50"
      style={{ borderLeft: `6px solid ${card.color}` }}
      onClick={handleRowClick}
    >
      {columnsWithActions.map((column) => {
        if (column.id === "actions") {
          return (
            <TableCell
              key={column.id}
              style={{
                width: `${column.width}px`,
                minWidth: `${column.minWidth}px`,
                textAlign: column.align,
                boxSizing: "border-box",
              }}
              className="p-1"
              data-action-cell
              onMouseEnter={() => setActionCellHovered(true)}
              onMouseLeave={() => setActionCellHovered(false)}
            >
              <div
                className={cn(
                  "flex items-center justify-center gap-2 transition-opacity",
                  actionCellHovered
                    ? "opacity-100"
                    : "opacity-60 hover:opacity-100"
                )}
              >
                {/* Star */}
                {!isCardColorEnabled && (
                  <CardStar
                    starred={card.isStarred}
                    onStarToggle={handleStarClick}
                  />
                )}

                {/* Notes */}
                {isCardNotesEnabled && shop && (
                  <NotesPopover
                    cardData={card}
                    disabled={disabled || isViewOnly}
                    iconSize={20}
                    shopId={shop.id}
                  />
                )}
                {/* Attachments */}
                {isAttachmentsEnabled && shop && (
                  <AttachmentsPopover
                    cardData={card}
                    disabled={disabled || isViewOnly}
                    iconSize={20}
                    shopId={shop.id}
                  />
                )}

                {/* Color Picker */}
                {isCardColorEnabled && shop && (
                  <ColorPicker
                    onColorChange={handleColorChange}
                    disabled={disabled || isViewOnly}
                    color={
                      card.color
                        ? card.color.includes("#")
                          ? (card.color as HexColor)
                          : `#${card.color}`
                        : DEFAULT_CARD_COLOR
                    }
                    triggerSize={"md"}
                  />
                )}

                {/* Menu */}
                {shop && (
                  <CardMenu
                    cardData={card}
                    disabled={disabled || isViewOnly}
                    employees={employees}
                    shop={shop}
                    triggerClassName={
                      "text-white/70 hover:text-white hover:bg-white/10 "
                    }
                    triggerIconSize={20}
                    onMoveToClick={onMoveToClick}
                  />
                )}
              </div>
            </TableCell>
          );
        }

        return (
          <TableCell
            key={column.id}
            style={{
              width: `${column.width}px`,
              minWidth: `${column.minWidth}px`,
              maxWidth: column.maxWidth ? `${column.maxWidth}px` : undefined,
              textAlign: column.align,
              boxSizing: "border-box",
              overflow: "hidden",
            }}
            className={`truncate ${column.className || ""} p-2`}
          >
            {formatCellValue(column, card)}
          </TableCell>
        );
      })}
    </TableRow>
  );
};
